import { z } from 'zod'
import sortBy from 'lodash/sortBy'
import { translateManager as t } from './TranslateManager'
import { DISCOUNT_TYPES } from '../stores/promoCodeStore/promoCode.consts'
import { getBuyoverTiersText } from '../features/cart/buyoverTiers'
import { type BuyOverDiscountTier } from '../components/Pricing/types/discount.types'
import { calculateDiscountPercent } from '../utils/utils'
import {
  MATERIAL_TYPES,
  FRAME_COLORS,
  TILE_SIZES,
  type PricingProduct,
  extractTilePropertiesFromSKU,
  BookSize,
} from '@mixtiles/web-backend-shared'
import { type FetchPricingItemResponse } from '../components/Pricing/types/PricingProvider.types'
import { PRODUCT_TYPES } from './ProductTypeState'

interface DiscountTextAndPercentage {
  text: string
  value: number
}

// Voucher metadata is free-form backoffice JSON; anything malformed falls back
// to the single-tier copy.
const VoucherBuyoverMetadataSchema = z.object({
  tiers: z
    .array(
      z.object({
        buyOverPrice: z.coerce.number(),
        buyOverDiscount: z.coerce.number().transform(Math.floor),
      })
    )
    .min(2)
    .transform((tiers) => sortBy(tiers, 'buyOverPrice')),
})

function getBuyoverMetadataTiers(
  metadata: unknown
): BuyOverDiscountTier[] | null {
  const parsed = VoucherBuyoverMetadataSchema.safeParse(metadata)
  return parsed.success ? parsed.data.tiers : null
}

export const getDiscountTextAndPercentage = (
  discount: any,
  type: string,
  productTranslationKey: string,
  originalBundlePrice: number | undefined,
  formatPrice: (value: number) => string
): DiscountTextAndPercentage => {
  switch (type) {
    case DISCOUNT_TYPES.BUYOVER:
      const tiers = getBuyoverMetadataTiers(discount?.metadata)
      if (tiers) {
        return {
          text: getBuyoverTiersText(
            tiers,
            (key, params) => t.get(key, params),
            formatPrice
          ),
          // "Best discount" comparisons rank by the ladder's largest tier.
          value: tiers[tiers.length - 1].buyOverDiscount,
        }
      }
      const buyoverDiscount = Math.floor(
        Number(discount?.metadata?.buyOverDiscount ?? 0)
      )
      if (discount.metadata.buyOverPrice > 0) {
        return {
          text: t.get(
            'order.checkout.payment_summary_rows.buy_over_percent_off_orders_over_price',
            {
              v1: buyoverDiscount,
              v2: `${formatPrice(discount.metadata.buyOverPrice)}`,
            }
          ),
          value: buyoverDiscount,
        }
      } else {
        return {
          text: t.get(
            'order.checkout.payment_summary_rows.discount_percentage',
            {
              v1: buyoverDiscount,
            }
          ),
          value: buyoverDiscount,
        }
      }

    case DISCOUNT_TYPES.BUYXGETY:
      const { buy, get } = discount?.metadata
      const percentOff = Math.round((get / (buy + get)) * 100)
      return {
        text: t.get(
          'general.promo_code_success_dialog.save_up_to_message_shortened',
          { v1: discount?.metadata?.percentOff || percentOff.toFixed(2) }
        ),
        value: discount?.metadata?.percentOff || percentOff,
      }
    case DISCOUNT_TYPES.PERCENT:
      if (discount?.metadata?.productType) {
        return {
          text: t.get(
            'general.promo_code_success_dialog.save_message_on_product',
            {
              v1: `${discount?.discount?.percent_off}%`,
              v2: t.get(productTranslationKey),
            }
          ),
          value: discount?.discount?.percent_off,
        }
      }
      return {
        text: t.get(
          'general.promo_code_success_dialog.save_up_to_message_shortened',
          { v1: discount?.discount?.percent_off }
        ),
        value: discount?.discount?.percent_off,
      }
    case DISCOUNT_TYPES.BUNDLE:
    default:
      return {
        text: t.get(
          'general.promo_code_success_dialog.save_up_to_message_shortened',
          {
            v1: `${calculateDiscountPercent(
              discount?.metadata?.bundlePrice,
              originalBundlePrice
            )}`,
          }
        ),
        value: originalBundlePrice
          ? calculateDiscountPercent(
              discount?.metadata?.bundlePrice,
              originalBundlePrice
            )
          : 0,
      }
  }
}

const safeJsonParse = (value: string) => {
  try {
    return JSON.parse(value)
  } catch (_) {
    return null
  }
}

function defaultPricingProduct(productType: PRODUCT_TYPES): PricingProduct {
  if (productType === PRODUCT_TYPES.PHOTO_BOOK) {
    return {
      tileSize: BookSize.BOOK_8X8 as unknown as TILE_SIZES,
    }
  } else {
    return {
      tileSize: TILE_SIZES.SQUARE_8X8,
      materialType: MATERIAL_TYPES.CLASSIC,
      frameColor: FRAME_COLORS.NONE,
    }
  }
}

// Helper function to get pricing value for a specific metadata variant.
function getVariantPricingValue(
  metadataKey: string,
  fetchPricingItem: (
    pricingProduct: PricingProduct,
    source: string
  ) => FetchPricingItemResponse,
  relevantProductType: PRODUCT_TYPES
): number {
  const pricingProduct =
    metadataKey === 'DEFAULT'
      ? defaultPricingProduct(relevantProductType)
      : extractTilePropertiesFromSKU(metadataKey)

  const pricingResponse = fetchPricingItem(
    pricingProduct,
    'promoCodeTextGenerator/getVariantPricingValue'
  )
  return pricingResponse.isLoading || !pricingResponse.value
    ? 0
    : pricingResponse.value
}

const isMixCouponIsDefaultOnly = (discount: {
  metadata: { [key: string]: string }
}) => {
  return Object.entries(discount.metadata).every(([key, value]) => {
    return key === 'DEFAULT' || !safeJsonParse(value)?.discountType
  })
}

// Helper function to determine productType from SKU
const getProductTypeFromSku = (sku: string): PRODUCT_TYPES | null => {
  if (sku.startsWith('BOOK_')) {
    return PRODUCT_TYPES.PHOTO_BOOK
  }

  return PRODUCT_TYPES.CLASSIC
}

export const getBestDiscountFromMixType = (
  discount: any,
  productTranslationKey: string,
  fetchPricingItem: (
    pricingProduct: PricingProduct,
    source: string
  ) => FetchPricingItemResponse,
  formatPrice: (value: number) => string,
  relevantProductType: PRODUCT_TYPES,
  currency: string
): DiscountTextAndPercentage => {
  // Check if this is version 2 (currency-based) MIX coupon
  const isVersion2 = parseInt(discount.metadata.version) === 2

  let metadata = discount.metadata
  if (isVersion2) {
    // Version 2 - currency-based implementation
    metadata = discount.metadata[currency] || discount.metadata.USD || {}
  }

  // Version 2 is per currency: each currency has a json containing version 1 structure:
  // example metadata:
  // {
  //   "ILS": {
  //   "DEFAULT": "{\"discountType\":\"BUYOVER\",\"buyOverPrice\":0,\"buyOverDiscount\":20,\"bundleCurrency\":\"ILS\"}",
  //     "SQUARE_8X8": "{\"bundlePrice\":120,\"bundleTiles\":8,\"bundleCurrency\":\"ILS\",\"discountType\":\"BUNDLE\"}"
  // },
  //   "USD": {
  //   "DEFAULT": "{\"discountType\":\"BUYOVER\",\"buyOverPrice\":120,\"buyOverDiscount\":10,\"bundleCurrency\":\"USD\"}",
  //     "SQUARE_8X8": "{\"bundlePrice\":100,\"bundleTiles\":10,\"bundleCurrency\":\"USD\",\"discountType\":\"BUNDLE\"}"
  // },
  //   "discountType": "MIX",
  //   "emailOfRequestingRepresentative": "chen@mixtiles.com",
  //   "productType": "classic",
  //   "version": "2"
  // }

  // Version 1 (backward compatibility) - original implementation
  // Get the maximum discount from a MIX type discount
  // Example metadata:
  // {DEFAULT: "{\"buyOverPrice\":10,\"discountType\":\"BUYOVER\",\"buyOverDiscount\":30}",
  // SQUARE_8X8: "{\"buyOverPrice\":10,\"discountType\":\"BUYOVER\",\"buyOverDiscount\":40}",
  // SQUARE_8X8-classic-wood: "{\"bundlePrice\":108,\"bundleTiles\":16,...}",
  // BOOK_8X8: "{\"buyOverPrice\":0,\"buyOverDiscount\":25,...}",
  // currency: "USD",
  // discountType: "MIX"}
  const discountTextAndPercentage = Object.entries(metadata).reduce(
    (
      acc: DiscountTextAndPercentage,
      [metadataKey, innerDiscountRaw]: [string, any]
    ) => {
      // Only process SKUs that belong to the relevant productType
      if (metadataKey !== 'DEFAULT') {
        const skuProductType = getProductTypeFromSku(metadataKey)
        if (skuProductType !== relevantProductType) {
          return acc
        }
      }

      let innerDiscount
      try {
        innerDiscount = JSON.parse(innerDiscountRaw)
      } catch (_) {
        return acc
      }

      // Get the correct pricing for this specific variant
      const variantPricingValue = getVariantPricingValue(
        metadataKey,
        fetchPricingItem,
        relevantProductType
      )

      if (variantPricingValue === 0) {
        return acc
      }

      const textAndPercentage = getDiscountTextAndPercentage(
        { metadata: innerDiscount },
        innerDiscount.discountType,
        productTranslationKey,
        variantPricingValue * (innerDiscount.bundleTiles || 1),
        formatPrice
      )

      if (acc.value < textAndPercentage.value) {
        return textAndPercentage
      } else {
        return acc
      }
    },
    { text: '', value: -1 }
  )

  if (isMixCouponIsDefaultOnly(discount)) {
    return discountTextAndPercentage
  } else {
    return {
      text: t.get(
        'general.promo_code_success_dialog.save_up_to_message_shortened',
        { v1: discountTextAndPercentage.value }
      ),
      value: discountTextAndPercentage.value,
    }
  }
}
